/* eslint-disable */
import React, { Suspense, useMemo, useState } from 'react';
import SVG from 'react-inlinesvg';
import { csrfToken } from '@rails/ujs';


import { Link, NavLink, useLocation } from 'react-router-dom';
import SupportIcon from '../../../../assets/icons/question-mark-circle.svg';
import SignOutIcon from '../../../../assets/icons/exit.svg';
import { getSideBarMenuList } from '../helpers/localData';
import useAuth from '../context/AuthContext';
import useData from '../context/DataContext';
import { SidebarHeaderLogoIcon } from './Header';
import useResponsive from '../hooks/useResponsive';

import ChevronRightIcon from '../../../../assets/images/reskin-images/icon--chevron-right.svg';

export default function SideBar({
  showLogo = true,
  className = "hidden md:flex flex-col border-r border-grey-light",
  alwaysShowText = false,
  onItemClick,
}) {
  const { isTenant, tenant, userData, isAdmin } = useAuth();
  const { contentData } = useData();
  const isSingleModule = useMemo(() => contentData?.modules?.length === 1, [contentData]);

  const blankPicture = useMemo(() => userData?.profile?.avatar?.url?.includes('blank'), [userData]);

  if(!userData) return (<></>);

  return (
    <aside className={className}>
      {showLogo && (
        <div className="flex flex-col px-6 py-5 lg:py-6 border-b border-grey-light">
          <SidebarHeaderLogoIcon />
        </div>
      )}
      <div className="flex flex-col flex-grow justify-between p-4">
        <div className="flex flex-col lg:items-center gap-2">
          {getSideBarMenuList(
            isTenant,
            tenant,
            isSingleModule,
            userData?.is_alex_available
          ).filter((menuItem) => menuItem.role !== "admin" || isAdmin)
          .map((menuItem) => (
              <SideBarMenuButton
                key={menuItem.text}
                {...menuItem}
                alwaysShowText={alwaysShowText}
                onItemClick={onItemClick}
              />
          ))}

        </div>
        <div className="flex flex-col gap-2 pt-2 border-t border-grey-lighter">
          <SideBarMenuLinkButton
            icon={SupportIcon}
            text="Support"
            path="/v2/contact-us"
            alwaysShowText={alwaysShowText}
            onItemClick={onItemClick}
          />

          <NavLink
            to="/v2/users/profile"
            onClick={onItemClick}
            className={({ isActive }) =>
              `group relative flex flex-row items-center px-4 py-2 gap-3 ${isActive ? 'rounded-lg bg-purple-100 font-bold text-link-purple' : 'text-black'}`
            }
          >
            <div className="flex w-4 items-center justify-center">
              {userData && (
                <>
                  {blankPicture ? (
                    userData?.profile?.first_name?.charAt(0)
                  ) : (
                    <img src={userData?.profile?.avatar?.thumb?.url} alt="user profile" className="w-5 h-5 rounded-full object-cover" />
                  )}
                </>
              )}
            </div>
            <div className={alwaysShowText ? "font-lg" : "font-lg hidden lg:block"}>Profile</div>
            {!alwaysShowText && (
              <div
                className="absolute left-full ml-2 px-2.5 py-1.5 bg-[#3c3c3c] text-white text-xs font-bold rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 lg:hidden"
                style={{ top: '50%', transform: 'translateY(-50%)' }}
              >
                Profile
              </div>
            )}
          </NavLink>

          <SideBarExitButton
            icon={SignOutIcon}
            text="Sign out"
            isTenant={isTenant}
            alwaysShowText={alwaysShowText}
            onItemClick={onItemClick}
          />
        </div>

      </div>
    </aside>
  );
}

function SideBarMenuButton(attr) {
  if (attr.subList) {
    return <SideBarMenuListButton {...attr} />;
  }
  return <SideBarMenuLinkButton {...attr} />;
}

function SideBarMenuListButton({ icon, text, path, target, subList, alwaysShowText = false, onItemClick }) {
  const { isLg } = useResponsive();
  const { pathname } = useLocation();
  const isChildActive = useMemo(() => (subList ? subList.some((subItem) => subItem.path === pathname) : false), [subList, pathname]);
  const [isOpen, setIsOpen] = useState(isChildActive);

  return (
    <div className="group relative w-full">
      <div
        className={`flex flex-row items-center justify-between px-4 py-2 gap-3 cursor-pointer ${
          isChildActive ? 'rounded-lg bg-purple-100 font-bold text-link-purple' : 'text-black'
        }`}
        style={{ width: isLg || alwaysShowText ? (alwaysShowText ? '100%' : '223px') : '' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-row items-center gap-3">
          <div className="flex w-4 items-center justify-center">
            <SVG src={icon} alt="home" className="fill-current" />
          </div>
          <div className={alwaysShowText ? "font-lg" : "font-lg hidden lg:block"}>{text}</div>
        </div>
      </div>
      {!alwaysShowText && (
        <div
          className="absolute left-full ml-2 px-2.5 py-1.5 bg-[#3c3c3c] text-white text-xs font-bold rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 lg:hidden"
          style={{ top: '22px', transform: 'translateY(-50%)' }}
        >
          {text}
        </div>
      )}
      {isOpen && (
        <div className="flex flex-col ml-2 mt-2">
          {subList.map((subItem) => (
            <SideBarMenuLinkButton
              key={subItem.path}
              {...subItem}
              width={alwaysShowText ? "100%" : ""}
              onClick={() => {
                setIsOpen(false);
                if (onItemClick) onItemClick();
              }}
              alwaysShowText={alwaysShowText}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SideBarMenuLinkButton({ icon, text, path, target, width = '223px', onClick, onItemClick, alwaysShowText = false }) {
  const { isLg } = useResponsive();
  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (onItemClick) onItemClick(e);
  };

  return (
    <NavLink
      to={path}
      target={target}
      className={({ isActive }) =>
        `group relative flex flex-row items-center ${alwaysShowText ? "px-4 py-2" : "px-4 py-2"} gap-3 ${isActive ? 'rounded-lg bg-purple-100 font-bold text-link-purple' : 'text-black'}`
      }
      style={{ width: isLg || alwaysShowText ? (alwaysShowText ? '100%' : width) : '' }}
      onClick={handleClick}
      end
    >
      <div className="flex w-4 items-center justify-center">
        <SVG src={icon} alt="home" className="fill-current" />
      </div>
      <div className={alwaysShowText ? "font-lg" : "font-lg hidden lg:block"}>{text}</div>
      {!alwaysShowText && (
        <div
          className="absolute left-full ml-2 px-2.5 py-1.5 bg-[#3c3c3c] text-white text-xs font-bold rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 lg:hidden"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          {text}
        </div>
      )}
    </NavLink>
  );
}


function SideBarExitButton({ icon, text, isTenant, alwaysShowText = false, onItemClick }) {
  const { isLg } = useResponsive();

  return (
    <div className="group relative w-full">
      <div
        className="flex flex-row items-center px-4 py-2 gap-3 text-black cursor-pointer"
        style={{ width: isLg || alwaysShowText ? (alwaysShowText ? '100%' : '223px') : '' }}
        onClick={
          () => {
            if (onItemClick) onItemClick();
            fetch('/users/sign_out', {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-Token': csrfToken()
              }
            }).then(() => {
              window.location.href = isTenant
                ? '/users/sign_in'
                : `${window.ENV?.SSO_CLIENT ||
                        'https://sso.admiredleadership.com'}/access`;
            });
          }}
      >
        <div className="flex w-4 items-center justify-center">
          <SVG src={icon} alt="home" className="fill-current" />
        </div>
        <div className={alwaysShowText ? "font-lg" : "font-lg hidden lg:block"}>{text}</div>
      </div>
      {!alwaysShowText && (
        <div
          className="absolute left-full ml-2 px-2.5 py-1.5 bg-[#3c3c3c] text-white text-xs font-bold rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 lg:hidden"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          {text}
        </div>
      )}
    </div>
  );
}
